import { Add, Link, Lock, LockOpen } from '@mui/icons-material';
import { Box, Card, Divider, Grid2, Typography } from '@mui/material';
import ButtonComponent from 'components/ButtonComponent';
import { useContext, useEffect, useState } from 'react';
import {
  useArmCompleteVirtualAlarmCentral,
  useDisarmCompleteVirtualAlarmCentral,
  useGetAllPartitionsByVirtualAlarmCentralId,
} from 'services/AlarmCentralService/Virtual/hooks/useMutation';
import {
  IAlarmCentralEventProps,
  IPartitionProps,
} from 'services/AlarmCentralService/Virtual/types';
import { AlarmCentralVirtualContext } from '../../context/AlarmCentralVirtualContext';
import AlarmCentralEventsList from '../_components/AlarmCentralEventsList';
import VirtualCentralDeviceManagementModal from './_components/VirtualAlarmCentralDeviceManagementModal';
import VirtualAlarmCentralPartitions from './_components/VirtualAlarmCentralPartitions';
import VirtualAlarmCentralPartitionManagementModal from './_components/VirtualAlarmCentralPartitionManagementModal';
import { AppContext } from 'context/AppContextProvider';
import AlarmCentralHeaderActionContainer from '../_components/AlarmCentralHeaderActionContainer';
import AlarmCentralResume from '../_components/AlarmCentralResumeCard';

const AlarmCentralManagementVirtualPage = () => {
  const { alarmCentral, requestAlarmCentral } = useContext(
    AlarmCentralVirtualContext,
  );
  const { showSnackbar } = useContext(AppContext);
  const [lastEvent, setLastEvent] = useState<
    IAlarmCentralEventProps | undefined
  >(undefined);

  const [
    isVisibleVirtualAlarmCentralPartitionManagementModal,
    setIsVisibleVirtualAlarmCentralPartitionManagementModal,
  ] = useState(false);
  const [
    isOpenAlarmCentralDeviceManagementModal,
    setIsOpenAlarmCentralDeviceManagementModal,
  ] = useState(false);
  const [partitions, setPartitions] = useState<IPartitionProps[]>([]);

  const { mutateAsync: mutateGetAllPartitionsByVirtualAlarmCentralIdAsync } =
    useGetAllPartitionsByVirtualAlarmCentralId();

  const {
    mutateAsync: mutateArmCompleteVirtualAlarmCentral,
    isLoading: isLoadingArmCompleteVirtualAlarmCentral,
  } = useArmCompleteVirtualAlarmCentral();
  const {
    mutateAsync: mutateDisarmCompleteVirtualAlarmCentral,
    isLoading: isLoadingDisarmCompleteVirtualAlarmCentral,
  } = useDisarmCompleteVirtualAlarmCentral();

  const requestPartitions = () => {
    if (alarmCentral && alarmCentral.id) {
      mutateGetAllPartitionsByVirtualAlarmCentralIdAsync(alarmCentral.id).then(
        setPartitions,
      );
    }
  };

  const armOrDisarmComplete = async (isArm: boolean) => {
    if (alarmCentral.id) {
      if (isArm) {
        await mutateArmCompleteVirtualAlarmCentral(alarmCentral.id).then(() => {
          showSnackbar('Central armada com sucesso.', 'success');
          requestAlarmCentral();
        });
      } else {
        await mutateDisarmCompleteVirtualAlarmCentral(alarmCentral.id).then(
          () => {
            showSnackbar('Central desarmada com sucesso.', 'success');
          },
        );
      }
      requestAlarmCentral();
    }
  };

  useEffect(() => {
    requestPartitions();
  }, [alarmCentral]);

  const statusText =
    partitions && partitions.length > 0 && partitions[0].isArmed
      ? 'Armada'
      : 'Desarmada';

  const statusTextAndShieldColor =
    partitions && partitions.length > 0 && partitions[0].isArmed
      ? 'error'
      : 'success';

  return (
    <Grid2 container columnSpacing={2}>
      <AlarmCentralHeaderActionContainer
        name={alarmCentral.name}
        shieldColor={statusTextAndShieldColor}
        statusTextColor={statusTextAndShieldColor}
        statusText={statusText}
        isLoadingArmOrDisarmCompleteAlarmCentral={
          isLoadingDisarmCompleteVirtualAlarmCentral ||
          isLoadingArmCompleteVirtualAlarmCentral
        }
        onClickArmOrDisarmButton={() =>
          armOrDisarmComplete(!partitions[0].isArmed)
        }
        iconArmOrDisarmButton={
          partitions[0] && !partitions[0].isArmed ? <Lock /> : <LockOpen />
        }
        isTotalArmed={partitions[0] && partitions[0].isArmed}
      />

      <AlarmCentralResume
        name={alarmCentral.name}
        isVirtual
        alarmCentral={alarmCentral}
        lastEventDate={lastEvent?.createdAt}
        isConnected
      />

      <Grid2 size={{ xs: 12, xl: 8 }} sx={{ mt: 2 }}>
        <Card
          sx={{ padding: 2 }}
          style={{
            border: '1px solid rgb(47, 57, 87)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }}>PARTIÇÕES</Typography>
            <Box>
              <ButtonComponent
                size={'small'}
                onClick={() =>
                  setIsVisibleVirtualAlarmCentralPartitionManagementModal(true)
                }
                sx={{ mr: 1 }}
                startIcon={<Add />}
              >
                Criar Partição
              </ButtonComponent>
              <ButtonComponent
                size={'small'}
                color="secondary"
                startIcon={<Link />}
                onClick={() => setIsOpenAlarmCentralDeviceManagementModal(true)}
              >
                Vincular dispositivos às partições
              </ButtonComponent>
            </Box>
            <VirtualCentralDeviceManagementModal
              partitions={partitions}
              isOpen={isOpenAlarmCentralDeviceManagementModal}
              onClose={() => setIsOpenAlarmCentralDeviceManagementModal(false)}
            />
          </Box>
          <Divider sx={{ my: 2, backgroundColor: 'rgb(47, 57, 87)' }} />
          {alarmCentral.isPartitionable && alarmCentral.id ? (
            <VirtualAlarmCentralPartitions
              partitions={partitions}
              alarmCentralId={alarmCentral.id}
              onArmOrDisarmPartition={requestPartitions}
              onDeletePartition={requestPartitions}
            />
          ) : (
            <Typography>Central não particionada.</Typography>
          )}
        </Card>
      </Grid2>

      <AlarmCentralEventsList
        alarmCentral={alarmCentral}
        onGetLastEvent={(ev) => setLastEvent(ev)}
      />

      <VirtualAlarmCentralPartitionManagementModal
        partitions={partitions}
        virtualAlarmCentralId={alarmCentral.id ? alarmCentral.id : 0}
        onClose={() => {
          setIsVisibleVirtualAlarmCentralPartitionManagementModal(false);
          requestPartitions();
        }}
        isOpen={isVisibleVirtualAlarmCentralPartitionManagementModal}
      />
    </Grid2>
  );
};

export default AlarmCentralManagementVirtualPage;
